import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, Title, Text, Metric, Grid, Table, TableHead, TableRow, TableHeaderCell, TableBody, TableCell } from '@tremor/react';
import {
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Legend,
} from 'recharts';
import kpiAPI from '../../api/kpi';
import logoMcg from '../../assets/logo.png';
import logoMan from '../../assets/man białe.png';
import './KPIStatPage.css';
import './KPIDashboard.css';

const PROJECT = 'MAN';

/** Oś X z dniami miesiąca – każda etykieta (wcześniej interval={2} pokazywał co ~3. dzień przy wszystkich słupkach). */
const KPI_DAILY_X_AXIS = {
  dataKey: 'label',
  interval: 0,
  tick: { fill: '#cbd5e1', fontSize: 12, fontWeight: 500, angle: -40, textAnchor: 'end' },
  height: 56,
};

/**
 * Recharts 3: podczas animacji słupka Bar wyłącza kontekst LabelList (showLabels = !isAnimating).
 * W panelu Podsumowania pigułki wtedy nie renderują się lub „nie wracają” po otwarciu innego KPI niż PICK.
 */
const KPI_BAR_LABELS_ALWAYS = { isAnimationActive: false };

/** Zgodne z margin.left/right BarChart – sumy HTML w jednej linii z centrami słupków. */
const KPI_DWA_BAR_MARGIN = { left: 48, right: 16, bottom: 48 };
const KPI_DWA_MARGIN_DAILY = { top: 36, ...KPI_DWA_BAR_MARGIN };
const KPI_DWA_MARGIN_STACK = { top: 42, ...KPI_DWA_BAR_MARGIN };
const KPI_OSOBNE_BAR_MARGIN = { top: 28, ...KPI_DWA_BAR_MARGIN };

/**
 * Sumy nad słupkami jako HTML (nad SVG) – Recharts 3 często nie pokazuje LabelList w tym layoucie (clip/showLabels).
 */
function KpiDailyTotalsHtmlRow({ data, field, color, marginLeft = KPI_DWA_BAR_MARGIN.left, marginRight = KPI_DWA_BAR_MARGIN.right, hideWhenZero = true }) {
  if (!data?.length) return null;
  return (
    <div
      className="kpi-daily-totals-html-row"
      style={{ paddingLeft: marginLeft, paddingRight: marginRight, color }}
      aria-hidden
    >
      {data.map((row, i) => {
        const raw = row[field];
        const n = Number(raw);
        if (raw == null || raw === '' || Number.isNaN(n)) {
          return <span key={i} className="kpi-daily-totals-html-cell" />;
        }
        const show = !hideWhenZero || n !== 0;
        return (
          <span key={i} className="kpi-daily-totals-html-cell">
            {show ? Math.round(n) : ''}
          </span>
        );
      })}
    </div>
  );
}

/** Ostatni segment stosu z wartością > 0 (od „dachu” w dół) – pod pigułkę sumy. */
function topStackSegmentDataKey(payload, stackKeysBottomToTop) {
  if (!payload || !stackKeysBottomToTop?.length) return null;
  for (let i = stackKeysBottomToTop.length - 1; i >= 0; i--) {
    const k = stackKeysBottomToTop[i];
    if (Number(payload[k] ?? 0) > 0) return k;
  }
  return stackKeysBottomToTop[stackKeysBottomToTop.length - 1];
}

/** Wspólne pozycjonowanie etykiet nad słupkiem (Recharts 3 – x/y często w viewBox). */
function barLabelGeometry(p) {
  const { value, payload, dataKey, viewBox: vbIn } = p;
  const val = value ?? payload?.[dataKey];
  if (val == null || val === '') return null;
  const vb = vbIn || {};
  const nx = Number(p.x != null ? p.x : vb.x);
  const ny = Number(p.y != null ? p.y : vb.y);
  const barW = p.width != null ? p.width : vb.upperWidth ?? vb.width;
  if (Number.isNaN(nx) || Number.isNaN(ny)) return null;
  const str = String(Math.round(Number(val)));
  const barCenterX = barW != null && !Number.isNaN(Number(barW)) ? nx + Number(barW) / 2 : nx;
  return { str, barCenterX, ny };
}

/** Etykieta wartości na wykresie – kompaktowa „pigułka” z liczbą, wycentrowana nad słupkiem */
function ChartValueLabel(p) {
  const { color } = p;
  const g = barLabelGeometry(p);
  if (!g) return null;
  const { str, barCenterX, ny } = g;
  const fill = color || '#f1f5f9';
  const w = Math.max(32, str.length * 11);
  const h = 26;
  return (
    <g transform={`translate(${barCenterX - w / 2},${ny - h - 10})`}>
      <rect width={w} height={h} rx="13" ry="13" fill="rgba(30,41,59,0.96)" stroke={fill} strokeWidth="1.5" />
      <text x={w / 2} y={h / 2 + 1} textAnchor="middle" dominantBaseline="middle" fill={fill} fontSize="14" fontWeight="600">
        {str}
      </text>
    </g>
  );
}

/**
 * Suma nad stosem – LabelList na każdym segmencie, rysuje tylko na wierzchołku (ostatni segment > 0).
 */
function StackedBarSumLabelList({ stackKeysBottomToTop, segmentKey, sumColor }) {
  return (
    <LabelList
      dataKey={segmentKey}
      position="top"
      offset={8}
      content={(p) => {
        const sum = p.payload?._sumLabel;
        if (sum == null || sum === '') return null;
        if (Number(sum) === 0) return null;
        const topKey = topStackSegmentDataKey(p.payload, stackKeysBottomToTop);
        if (segmentKey !== topKey) return null;
        return <ChartValueLabel {...p} value={sum} dataKey="_sumLabel" color={sumColor} />;
      }}
    />
  );
}

function normalizeSheetData(data) {
  if (!data || !Array.isArray(data)) return [];
  return data.map((row) => {
    if (!Array.isArray(row)) return [{ value: '' }];
    return row.map((cell) => {
      if (cell && typeof cell === 'object' && 'value' in cell) return cell;
      return { value: cell != null ? String(cell) : '' };
    });
  });
}

function parseDateOnly(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDateFromCell(val) {
  if (val == null || val === '') return null;
  const s = String(val).trim();
  if (!s) return null;
  const datePart = s.includes(' ') ? s.split(/\s/)[0] : s;
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(datePart);
  if (iso) return new Date(iso[0]);
  const ddmmyy = datePart.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (ddmmyy) return new Date(parseInt(ddmmyy[3], 10), parseInt(ddmmyy[2], 10) - 1, parseInt(ddmmyy[1], 10));
  const d = new Date(datePart);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getColumnIndex(headers, name) {
  const needle = String(name || '').trim().toLowerCase();
  const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase() === needle);
  return idx >= 0 ? idx : -1;
}

/** Szuka kolumny po kilku możliwych nazwach (np. MAGAZYN, Magazyn) lub po częściowym dopasowaniu */
function getColumnIndexFlex(headers, ...names) {
  for (const name of names) {
    const idx = getColumnIndex(headers, name);
    if (idx >= 0) return idx;
  }
  const needle = String(names[0] || '').trim().toLowerCase();
  if (needle) {
    const idx = headers.findIndex((h) => String(h || '').trim().toLowerCase().includes(needle));
    if (idx >= 0) return idx;
  }
  return -1;
}

function getDateColumnIndex(headers) {
  const names = headers.map((h) => String(h || '').trim().toUpperCase());
  const idx = names.findIndex(
    (n) => n === 'DATA WPISU' || n.includes('DATA WPISU') || n === 'DATA' || n === 'DATE' || n.startsWith('DATA')
  );
  return idx >= 0 ? idx : 0;
}

function parseNumber(val) {
  if (val == null || val === '') return 0;
  const v = typeof val === 'object' && val !== null && 'value' in val ? val.value : val;
  const n = Number(String(v).replace(/\s/g, '').replace(',', '.'));
  return Number.isNaN(n) ? 0 : n;
}

function dateToStr(d) {
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
}

/** PS według kodowania – Y = Opis Kodowania/Kod, X = liczba wpisów */
function buildOpisKodowaniaBarData(rows, headers) {
  const opisCol = getColumnIndex(headers, 'Opis Kodowania') >= 0 ? getColumnIndex(headers, 'Opis Kodowania') : getColumnIndex(headers, 'Kod');
  if (opisCol < 0) return [];
  const byOpis = new Map();
  rows.forEach((row) => {
    const opisVal = row[opisCol] && typeof row[opisCol] === 'object' ? row[opisCol].value : row[opisCol];
    const name = String(opisVal ?? '').trim() || '(pusty)';
    byOpis.set(name, (byOpis.get(name) || 0) + 1);
  });
  return Array.from(byOpis.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

/** PS według PSA – Y = PSA, X = liczba wpisów */
function buildPsaBarData(rows, headers) {
  const psaCol = getColumnIndex(headers, 'PSA');
  if (psaCol < 0) return [];
  const byPsa = new Map();
  rows.forEach((row) => {
    const psaVal = row[psaCol] && typeof row[psaCol] === 'object' ? row[psaCol].value : row[psaCol];
    const label = String(psaVal ?? '').trim() || '(pusty)';
    byPsa.set(label, (byPsa.get(label) || 0) + 1);
  });
  return Array.from(byPsa.entries())
    .map(([PSA, count]) => ({ PSA, count }))
    .sort((a, b) => b.count - a.count);
}

/** Filtruje wiersze PS do konkretnego miesiąca (monthStr = YYYY-MM). */
function filterPsRowsByMonth(rows, headers, monthStr) {
  if (!rows?.length || !headers?.length || !monthStr || monthStr.length < 7) return [];
  const [year, month] = monthStr.split('-').map(Number);
  if (!year || !month) return [];
  const dateCol = getDateColumnIndex(headers);
  return rows.filter((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return false;
    return d.getFullYear() === year && d.getMonth() === month - 1;
  });
}

function aggregateRaportByDay(rows, headers) {
  const dataCol = getColumnIndex(headers, 'DATA');
  const zmianaCol = getColumnIndex(headers, 'ZMIANA');
  if (dataCol < 0) return {};
  const bhpCol = getColumnIndex(headers, 'BHP');
  const pickCol = getColumnIndex(headers, 'ilość pick');
  const przepakCol = getColumnIndex(headers, 'PRZEPAK') >= 0 ? getColumnIndex(headers, 'PRZEPAK') : getColumnIndex(headers, 'ilość przepak');
  const shipSqCol = getColumnIndex(headers, 'SHIP SQ');
  const shipRepCol = getColumnIndex(headers, 'SHIP REP');
  const shipLergutCol = getColumnIndex(headers, 'SHIP LERGUT');
  const repPickCol = getColumnIndex(headers, 'REP PICK');
  const inboundCol = getColumnIndex(headers, 'INBOUND');
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    if (!byDay[dateStr]) {
      byDay[dateStr] = {
        bhp: 0, pick: 0, pickA: 0, pickB: 0, przepak: 0,
        shipSq: 0, shipRep: 0, shipLergut: 0,
        repPick: 0, repPickA: 0, repPickB: 0,
        inbound: 0, inboundA: 0, inboundB: 0,
      };
    }
    if (bhpCol >= 0) byDay[dateStr].bhp += parseNumber(row[bhpCol]);
    const z = zmianaCol >= 0 ? String((row[zmianaCol] && typeof row[zmianaCol] === 'object' ? row[zmianaCol].value : row[zmianaCol]) ?? '').trim().toUpperCase() : '';
    const isB = z === 'B';
    if (pickCol >= 0) {
      const v = parseNumber(row[pickCol]);
      byDay[dateStr].pick += v;
      if (isB) byDay[dateStr].pickB += v;
      else byDay[dateStr].pickA += v;
    }
    if (przepakCol >= 0) byDay[dateStr].przepak += parseNumber(row[przepakCol]);
    byDay[dateStr].shipSq += shipSqCol >= 0 ? parseNumber(row[shipSqCol]) : 0;
    byDay[dateStr].shipRep += shipRepCol >= 0 ? parseNumber(row[shipRepCol]) : 0;
    byDay[dateStr].shipLergut += shipLergutCol >= 0 ? parseNumber(row[shipLergutCol]) : 0;
    if (repPickCol >= 0) {
      const rv = parseNumber(row[repPickCol]);
      byDay[dateStr].repPick += rv;
      if (isB) byDay[dateStr].repPickB += rv;
      else byDay[dateStr].repPickA += rv;
    }
    if (inboundCol >= 0) {
      const inv = parseNumber(row[inboundCol]);
      byDay[dateStr].inbound += inv;
      if (isB) byDay[dateStr].inboundB += inv;
      else byDay[dateStr].inboundA += inv;
    }
  });
  return byDay;
}

/** PS: byDay[dateStr] = liczba wpisów (wierszy) na dzień */
function aggregatePsByDay(rows, headers) {
  const dateCol = getDateColumnIndex(headers);
  const byDay = {};
  rows.forEach((row) => {
    const val = row[dateCol] && typeof row[dateCol] === 'object' ? row[dateCol].value : row[dateCol];
    const d = parseDateFromCell(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    byDay[dateStr] = (byDay[dateStr] || 0) + 1;
  });
  return byDay;
}

/** Arkusz Zmagazynowane: CONF (data potwierdzenia), MAGAZYN, ILOŚĆ – agregacja po dacie z podziałem na magazyny */
function aggregateZmagazynowaneByDay(rows, headers) {
  const confCol =
    getColumnIndex(headers, 'CONF (Z DATAMI)') >= 0 ? getColumnIndex(headers, 'CONF (Z DATAMI)') :
    getColumnIndex(headers, 'CONF (DATA POTWIERDZENIA)') >= 0 ? getColumnIndex(headers, 'CONF (DATA POTWIERDZENIA)') :
    getColumnIndex(headers, 'CONF');
  const magazynCol = getColumnIndex(headers, 'MAGAZYN');
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (confCol < 0 || iloscCol < 0) return {};
  const byDay = {};
  let lastDateStr = null;
  rows.forEach((row) => {
    const val = row[confCol] && typeof row[confCol] === 'object' ? row[confCol].value : row[confCol];
    let d = parseDateFromCell(val) || parseDateOnly(val);
    if (d) {
      lastDateStr = dateToStr(d);
    }
    if (!lastDateStr) return;
    const dateStr = lastDateStr;
    if (!byDay[dateStr]) byDay[dateStr] = { total: 0, byMagazyn: {} };
    const magazyn = magazynCol >= 0
      ? String((row[magazynCol] && typeof row[magazynCol] === 'object' ? row[magazynCol].value : row[magazynCol]) ?? '').trim() || '(brak)'
      : '(brak)';
    const qty = parseNumber(row[iloscCol]);
    byDay[dateStr].total += qty;
    byDay[dateStr].byMagazyn[magazyn] = (byDay[dateStr].byMagazyn[magazyn] || 0) + qty;
  });
  return byDay;
}

/** Arkusz Wydanie: DATA, MAGAZYN, ILOŚĆ – agregacja po dacie z podziałem na magazyny.
 *  UWAGA: W arkuszu data może występować tylko w pierwszym wierszu grupy (scalone komórki).
 *  Puste komórki DATA oznaczają kontynuację poprzedniej daty – używamy ostatniej znanej daty. */
function aggregateWydanieByDay(rows, headers) {
  const dataCol = getColumnIndexFlex(headers, 'DATA', 'Data');
  const magazynCol = getColumnIndexFlex(headers, 'MAGAZYN', 'Magazyn');
  const iloscCol = getColumnIndexFlex(headers, 'ILOŚĆ', 'Ilość', 'ILOSC');
  if (dataCol < 0 || iloscCol < 0) return {};
  const byDay = {};
  let lastDate = null;
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateFromCell(val) || parseDateOnly(val);
    if (d) lastDate = d;
    if (!lastDate) return;
    const dateStr = dateToStr(lastDate);
    if (!byDay[dateStr]) byDay[dateStr] = { total: 0, byMagazyn: {} };
    let magazyn = '(brak)';
    if (magazynCol >= 0) {
      const raw = row[magazynCol] && typeof row[magazynCol] === 'object' ? row[magazynCol].value : row[magazynCol];
      magazyn = (raw != null && raw !== '') ? String(raw).trim() : '(brak)';
    }
    const qty = parseNumber(row[iloscCol]);
    byDay[dateStr].total += qty;
    byDay[dateStr].byMagazyn[magazyn] = (byDay[dateStr].byMagazyn[magazyn] || 0) + qty;
  });
  return byDay;
}

/** Arkusz Przepak: DATA, MAGAZYN, ILOŚĆ – agregacja po dacie z podziałem na magazyny.
 *  UWAGA: Data może występować tylko w pierwszym wierszu grupy (scalone komórki). */
function aggregatePrzepakByDay(rows, headers) {
  const dataCol = getColumnIndexFlex(headers, 'DATA', 'Data');
  const magazynCol = getColumnIndexFlex(headers, 'MAGAZYN', 'Magazyn');
  const iloscCol = getColumnIndexFlex(headers, 'ILOŚĆ', 'Ilość', 'ILOSC');
  if (dataCol < 0 || iloscCol < 0) return {};
  const byDay = {};
  let lastDate = null;
  rows.forEach((row) => {
    const val = row[dataCol] && typeof row[dataCol] === 'object' ? row[dataCol].value : row[dataCol];
    const d = parseDateFromCell(val) || parseDateOnly(val);
    if (d) lastDate = d;
    if (!lastDate) return;
    const dateStr = dateToStr(lastDate);
    if (!byDay[dateStr]) byDay[dateStr] = { total: 0, byMagazyn: {} };
    let magazyn = '(brak)';
    if (magazynCol >= 0) {
      const raw = row[magazynCol] && typeof row[magazynCol] === 'object' ? row[magazynCol].value : row[magazynCol];
      magazyn = (raw != null && raw !== '') ? String(raw).trim() : '(brak)';
    }
    const qty = parseNumber(row[iloscCol]);
    byDay[dateStr].total += qty;
    byDay[dateStr].byMagazyn[magazyn] = (byDay[dateStr].byMagazyn[magazyn] || 0) + qty;
  });
  return byDay;
}

/** Arkusz PICK: kolumny J, CONF (Z DATAMI), ILOŚĆ – agregacja ILOŚĆ po dacie z CONF */
function aggregatePickSheetByDay(rows, headers) {
  const confCol = getColumnIndex(headers, 'CONF (Z DATAMI)') >= 0 ? getColumnIndex(headers, 'CONF (Z DATAMI)') : getColumnIndex(headers, 'CONF');
  const iloscCol = getColumnIndex(headers, 'ILOŚĆ');
  if (confCol < 0 || iloscCol < 0) return {};
  const byDay = {};
  rows.forEach((row) => {
    const val = row[confCol] && typeof row[confCol] === 'object' ? row[confCol].value : row[confCol];
    const d = parseDateFromCell(val) || parseDateOnly(val);
    if (!d) return;
    const dateStr = dateToStr(d);
    byDay[dateStr] = (byDay[dateStr] || 0) + parseNumber(row[iloscCol]);
  });
  return byDay;
}

/** Agregacja miesięczna: sumuje wartości dla wszystkich dni w miesiącu YYYY-MM */
function aggregateByMonth(byDayRaport, byDayPs, byDayPick, byDayZmagazynowane, byDayWydanie, byDayPrzepak, monthStr) {
  const prefix = monthStr + '-';
  let bhp = 0, pick = 0, repPick = 0, przepak = 0, shipSq = 0, shipRep = 0, shipLergut = 0, inbound = 0, wydanie = 0;
  let psSum = 0;
  const hasPrzepak = byDayPrzepak && Object.keys(byDayPrzepak).length > 0;
  Object.keys(byDayRaport || {}).forEach((dateStr) => {
    if (!dateStr.startsWith(prefix)) return;
    const r = byDayRaport[dateStr];
    bhp += r.bhp ?? 0;
    repPick += r.repPick ?? 0;
    if (!hasPrzepak) przepak += r.przepak ?? 0;
    shipSq += r.shipSq ?? 0;
    shipRep += r.shipRep ?? 0;
    shipLergut += r.shipLergut ?? 0;
    const hasZmagazynowane = byDayZmagazynowane && Object.keys(byDayZmagazynowane).length > 0;
    if (!hasZmagazynowane) inbound += r.inbound ?? 0;
  });
  Object.keys(byDayZmagazynowane || {}).forEach((dateStr) => {
    if (!dateStr.startsWith(prefix)) return;
    inbound += byDayZmagazynowane[dateStr]?.total ?? 0;
  });
  Object.keys(byDayWydanie || {}).forEach((dateStr) => {
    if (!dateStr.startsWith(prefix)) return;
    wydanie += byDayWydanie[dateStr]?.total ?? 0;
  });
  Object.keys(byDayPrzepak || {}).forEach((dateStr) => {
    if (!dateStr.startsWith(prefix)) return;
    przepak += byDayPrzepak[dateStr]?.total ?? 0;
  });
  Object.keys(byDayPs || {}).forEach((dateStr) => {
    if (!dateStr.startsWith(prefix)) return;
    psSum += byDayPs[dateStr] ?? 0;
  });
  Object.keys(byDayPick || {}).forEach((dateStr) => {
    if (!dateStr.startsWith(prefix)) return;
    pick += byDayPick[dateStr] ?? 0;
  });
  const transport = shipSq + shipRep + shipLergut;
  return { bhp, pick, repPick, przepak, transport, inbound, psSum, wydanie };
}

/** Nazwa miesiąca po polsku (np. "Styczeń 2025") */
function formatMonthLabel(monthStr) {
  if (!monthStr || monthStr.length < 7) return monthStr || '';
  const [y, m] = monthStr.split('-').map(Number);
  const names = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];
  return `${names[(m || 1) - 1]} ${y || ''}`;
}

/** Dane do wykresu ZMAGAZYNOWANE – dzienne z podziałem na magazyny (stacked bar) */
function getZmagazynowaneChartData(monthStr, dataRef) {
  if (!monthStr || !dataRef?.current?.byDayZmagazynowane) return { data: [], magazyny: [] };
  const byDay = dataRef.current.byDayZmagazynowane;
  const daysInMonth = new Date(parseInt(monthStr.split('-')[0], 10), parseInt(monthStr.split('-')[1], 10), 0).getDate();
  const magazynSet = new Set();
  const data = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${monthStr}-${String(d).padStart(2, '0')}`;
    const dayData = byDay[dateStr];
    const total = dayData?.total ?? 0;
    const row = {
      label: `${String(d).padStart(2, '0')}.${monthStr.split('-')[1]}`,
      day: d,
      value: total,
      _sumLabel: total,
      _sumBar: 0,
    };
    if (dayData?.byMagazyn) {
      Object.entries(dayData.byMagazyn).forEach(([mag, qty]) => {
        magazynSet.add(mag);
        row[mag] = qty;
      });
    }
    data.push(row);
  }
  const magazyny = Array.from(magazynSet).sort();
  return { data, magazyny };
}

/** Dane do wykresu WYDANIE – dzienne z podziałem na magazyny (stacked bar) */
function getWydanieChartData(monthStr, dataRef) {
  if (!monthStr || !dataRef?.current?.byDayWydanie) return { data: [], magazyny: [] };
  const byDay = dataRef.current.byDayWydanie;
  const daysInMonth = new Date(parseInt(monthStr.split('-')[0], 10), parseInt(monthStr.split('-')[1], 10), 0).getDate();
  const magazynSet = new Set();
  Object.values(byDay).forEach((dayData) => {
    if (dayData?.byMagazyn) Object.keys(dayData.byMagazyn).forEach((m) => magazynSet.add(m));
  });
  const data = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${monthStr}-${String(d).padStart(2, '0')}`;
    const dayData = byDay[dateStr];
    const total = dayData?.total ?? 0;
    const row = {
      label: `${String(d).padStart(2, '0')}.${monthStr.split('-')[1]}`,
      day: d,
      value: total,
      _sumLabel: total,
      _sumBar: 0,
    };
    magazynSet.forEach((mag) => { row[mag] = dayData?.byMagazyn?.[mag] ?? 0; });
    data.push(row);
  }
  const magazyny = Array.from(magazynSet).sort((a, b) => String(a).localeCompare(String(b)));
  return { data, magazyny };
}

/** Dane do wykresu PRZEPAK – dzienne z podziałem na magazyny (stacked bar) */
function getPrzepakChartData(monthStr, dataRef) {
  const byDay = dataRef?.current?.byDayPrzepak;
  if (!monthStr || !byDay || Object.keys(byDay).length === 0) return { data: [], magazyny: [] };
  const daysInMonth = new Date(parseInt(monthStr.split('-')[0], 10), parseInt(monthStr.split('-')[1], 10), 0).getDate();
  const magazynSet = new Set();
  Object.values(byDay).forEach((dayData) => {
    if (dayData?.byMagazyn) Object.keys(dayData.byMagazyn).forEach((m) => magazynSet.add(m));
  });
  const data = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${monthStr}-${String(d).padStart(2, '0')}`;
    const dayData = byDay[dateStr];
    const total = dayData?.total ?? 0;
    const row = {
      label: `${String(d).padStart(2, '0')}.${monthStr.split('-')[1]}`,
      day: d,
      value: total,
      _sumLabel: total,
      _sumBar: 0,
    };
    magazynSet.forEach((mag) => { row[mag] = dayData?.byMagazyn?.[mag] ?? 0; });
    data.push(row);
  }
  const magazyny = Array.from(magazynSet).sort((a, b) => String(a).localeCompare(String(b)));
  return { data, magazyny };
}

/** Dane do wykresu dziennego dla wybranego KPI i miesiąca */
function getChartDataForKpi(kpi, monthStr, dataRef) {
  if (!kpi || !monthStr || !dataRef?.current) return [];
  const prefix = monthStr + '-';
  const { byDayRaport, byDayPs, byDayPick, byDayZmagazynowane, byDayWydanie, byDayPrzepak } = dataRef.current;
  const daysInMonth = new Date(parseInt(monthStr.split('-')[0], 10), parseInt(monthStr.split('-')[1], 10), 0).getDate();
  const result = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${monthStr}-${String(d).padStart(2, '0')}`;
    let value = 0;
    if (kpi === 'BHP') {
      const r = byDayRaport?.[dateStr];
      value = r?.bhp ?? 0;
    } else if (kpi === 'PS') {
      value = byDayPs?.[dateStr] ?? 0;
    } else if (kpi === 'ZMAGAZYNOWANE') {
      const z = byDayZmagazynowane?.[dateStr];
      const hasZmagazynowane = byDayZmagazynowane && Object.keys(byDayZmagazynowane).length > 0;
      value = hasZmagazynowane ? (z?.total ?? 0) : (byDayRaport?.[dateStr]?.inbound ?? 0);
    } else if (kpi === 'PICK') {
      value = byDayPick?.[dateStr] ?? 0;
    } else if (kpi === 'WYDANIE') {
      const w = byDayWydanie?.[dateStr];
      value = w?.total ?? 0;
    } else if (kpi === 'REPLENISHMENT') {
      const r = byDayRaport?.[dateStr];
      value = r?.repPick ?? 0;
    } else if (kpi === 'PRZEPAK') {
      const hasPrzepak = byDayPrzepak && Object.keys(byDayPrzepak).length > 0;
      value = hasPrzepak ? (byDayPrzepak?.[dateStr]?.total ?? 0) : (byDayRaport?.[dateStr]?.przepak ?? 0);
    } else if (kpi === 'TRANSPORT') {
      const r = byDayRaport?.[dateStr];
      value = (r?.shipSq ?? 0) + (r?.shipRep ?? 0) + (r?.shipLergut ?? 0);
    }
    result.push({
      label: `${String(d).padStart(2, '0')}.${monthStr.split('-')[1]}`,
      day: d,
      value,
    });
  }
  return result;
}

/** TRANSPORT z arkusza Raport: dzienne SQ + REP + LERGUT z możliwością stosu. */
function getTransportChartData(monthStr, dataRef) {
  if (!monthStr || !dataRef?.current?.byDayRaport) return { data: [] };
  const byDay = dataRef.current.byDayRaport;
  const daysInMonth = new Date(parseInt(monthStr.split('-')[0], 10), parseInt(monthStr.split('-')[1], 10), 0).getDate();
  const data = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${monthStr}-${String(d).padStart(2, '0')}`;
    const r = byDay[dateStr];
    const sq = r?.shipSq ?? 0;
    const rep = r?.shipRep ?? 0;
    const lergut = r?.shipLergut ?? 0;
    const total = sq + rep + lergut;
    data.push({
      label: `${String(d).padStart(2, '0')}.${monthStr.split('-')[1]}`,
      day: d,
      value: total,
      shipSq: sq,
      shipRep: rep,
      shipLergut: lergut,
      _sumLabel: total,
      _sumBar: 0,
    });
  }
  return { data };
}

/** Tooltip czytelny – jasne tło, ciemny tekst (moduł PS) */
function PsTooltipOpisKodowania({ active, payload }) {
  if (!active || !payload?.length) return null;
  const label = payload[0]?.payload?.name ?? '—';
  const value = payload[0]?.value ?? '—';
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>Ilość wpisów: {value}</span>
      </div>
    </div>
  );
}

function PsTooltipPsa({ active, payload }) {
  if (!active || !payload?.length) return null;
  const label = payload[0]?.payload?.PSA ?? '—';
  const value = payload[0]?.value ?? '—';
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-teal" />
        <span>Ilość wpisów: {value}</span>
      </div>
    </div>
  );
}

function PsTooltipDaily({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const value = payload[0]?.value ?? '—';
  const name = payload[0]?.name ?? 'Wartość';
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label ?? '—'}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-cyan" />
        <span>{name}: {value}</span>
      </div>
    </div>
  );
}

/** Tooltip dla ZMAGAZYNOWANE – stacked bar (suma + podział na magazyny) */
function ZmagazynowaneTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (Number(p.value) || 0), 0);
  return (
    <div className="kpi-ps-tooltip">
      <div className="kpi-ps-tooltip-header">{label}</div>
      <div className="kpi-ps-tooltip-row">
        <span className="kpi-ps-tooltip-dot kpi-ps-tooltip-dot-teal" />
        <span>Suma: {total}</span>
      </div>
      {payload.filter((p) => p.value > 0).map((p, i) => (
        <div key={i} className="kpi-ps-tooltip-row" style={{ marginTop: 4 }}>
          <span className="kpi-ps-tooltip-dot" style={{ background: p.color }} />
          <span>{p.name}: {p.value}</span>
        </div>
      ))}
    </div>
  );
}

const KPI_CHART_COLORS = {
  BHP: '#78909c',
  PS: '#42a5f5',
  ZMAGAZYNOWANE: '#9c27b0',
  PICK: '#00bcd4',
  WYDANIE: '#ff9800',
  REPLENISHMENT: '#d4a057',
  PRZEPAK: '#00bcd4',
  TRANSPORT: '#26a69a',
};

/** Kolejność segmentów stosu TRANSPORT (zgodna z Raportem) */
const TRANSPORT_STACK_SERIES = [
  { key: 'shipSq', name: 'SHIP SQ', color: '#26a69a' },
  { key: 'shipRep', name: 'SHIP REP', color: '#42a5f5' },
  { key: 'shipLergut', name: 'SHIP LERGUT', color: '#ff9800' },
];
/** Kolejność jak w JSX (dół → górę stosu) – pod wybór segmentu z pigułką sumy */
const TRANSPORT_STACK_KEYS_ORDER = TRANSPORT_STACK_SERIES.map((s) => s.key);

const MONTH_NAMES = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];
const MONTH_SHORT = ['sty', 'lut', 'mar', 'kwi', 'maj', 'cze', 'lip', 'sie', 'wrz', 'paź', 'lis', 'gru'];

/** Własny wybór miesiąca – ciemny motyw dopasowany do strony */
function MonthPicker({ value, availableMonths, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    if (value) return parseInt(value.split('-')[0], 10);
    return new Date().getFullYear();
  });
  const ref = useRef(null);

  useEffect(() => {
    if (value) setViewYear(parseInt(value.split('-')[0], 10));
  }, [value]);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const [valY, valM] = value ? value.split('-').map(Number) : [null, null];

  const pick = (y, m) => {
    const str = `${y}-${String(m).padStart(2, '0')}`;
    onChange(str);
    setOpen(false);
  };

  const minY = availableMonths.length ? parseInt(availableMonths[availableMonths.length - 1].split('-')[0], 10) : viewYear - 2;
  const maxY = availableMonths.length ? parseInt(availableMonths[0].split('-')[0], 10) : viewYear + 2;

  return (
    <div className={`kpi-month-picker-wrap ${open ? 'open' : ''}`} ref={ref}>
      <span className="kpi-dashboard-date-picker-label">Wybierz miesiąc</span>
      <button
        type="button"
        className="kpi-month-picker-trigger"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span>{value ? formatMonthLabel(value) : 'Wybierz miesiąc'}</span>
        <span className="kpi-month-picker-chevron">▼</span>
      </button>
      {open && (
        <div className="kpi-month-picker-dropdown">
          <div className="kpi-month-picker-year-row">
            <button type="button" className="kpi-month-picker-nav" onClick={() => setViewYear((y) => Math.max(minY, y - 1))} aria-label="Poprzedni rok">‹</button>
            <span className="kpi-month-picker-year">{viewYear}</span>
            <button type="button" className="kpi-month-picker-nav" onClick={() => setViewYear((y) => Math.min(maxY, y + 1))} aria-label="Następny rok">›</button>
          </div>
          <div className="kpi-month-picker-grid">
            {MONTH_SHORT.map((label, i) => {
              const m = i + 1;
              const str = `${viewYear}-${String(m).padStart(2, '0')}`;
              const isSelected = valY === viewYear && valM === m;
              const isAvailable = !availableMonths.length || availableMonths.includes(str);
              return (
                <button
                  key={m}
                  type="button"
                  className={`kpi-month-picker-cell ${isSelected ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}`}
                  onClick={() => isAvailable && pick(viewYear, m)}
                  disabled={!isAvailable}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div className="kpi-month-picker-actions">
            <button type="button" className="kpi-month-picker-action" onClick={() => { onChange(null); setOpen(false); }}>Wyczyść</button>
            <button type="button" className="kpi-month-picker-action" onClick={() => pick(new Date().getFullYear(), new Date().getMonth() + 1)}>Ten miesiąc</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Jawna wysokość (px) dla ResponsiveContainer w „Dwa wykresy” – height="100%" często daje 0 (pomiar rodzica / Tremor). */
function computeDwaChartPlotHeight() {
  if (typeof window === 'undefined') return 300;
  return Math.min(420, Math.max(220, Math.round(window.innerHeight * 0.28)));
}

function useDwaChartPlotHeight() {
  const [height, setHeight] = useState(computeDwaChartPlotHeight);
  useEffect(() => {
    const onResize = () => setHeight(computeDwaChartPlotHeight());
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return height;
}

function KPIPodsumowanie() {
  const dwaChartPlotHeight = useDwaChartPlotHeight();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [monthly, setMonthly] = useState({
    bhp: null,
    pick: null,
    repPick: null,
    przepak: null,
    transport: null,
    inbound: null,
    psSum: null,
    wydanie: null,
  });
  const dataRef = useRef({ byDayRaport: {}, byDayPs: {}, byDayPick: {}, byDayZmagazynowane: {}, byDayWydanie: {}, byDayPrzepak: {}, psRows: [], psHeaders: [] });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerKpi, setDrawerKpi] = useState(null);
  const chartPanelRef = useRef(null);
  const [zmagazynowaneChartView, setZmagazynowaneChartView] = useState('dwa');
  const [wydanieChartView, setWydanieChartView] = useState('dwa');
  const [przepakChartView, setPrzepakChartView] = useState('dwa');

  const loadData = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await kpiAPI.getReports(PROJECT);
      const list = Array.isArray(res?.sheets) ? res.sheets : [];
      const raportSheet = list.find((s) => String(s.name || '').trim() === 'Raport');
      const psSheet = list.find((s) => String(s.name || '').trim() === 'PS');
      const pickSheet = list.find((s) => String(s.name || '').trim().toUpperCase() === 'PICK');
      const zmagazynowaneSheet = list.find((s) => String(s.name || '').trim().toLowerCase() === 'zmagazynowane');
      const wydanieSheet = list.find((s) => String(s.name || '').trim().toLowerCase() === 'wydanie');
      const przepakSheet = list.find((s) => String(s.name || '').trim().toLowerCase() === 'przepak');

      const raportData = raportSheet ? normalizeSheetData(raportSheet.data) : [];
      const psData = psSheet ? normalizeSheetData(psSheet.data) : [];
      const pickData = pickSheet ? normalizeSheetData(pickSheet.data) : [];
      const zmagazynowaneData = zmagazynowaneSheet ? normalizeSheetData(zmagazynowaneSheet.data) : [];
      const wydanieData = wydanieSheet ? normalizeSheetData(wydanieSheet.data) : [];
      const przepakData = przepakSheet ? normalizeSheetData(przepakSheet.data) : [];
      const raportHeaders = raportData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const psHeaders = psData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const pickHeaders = pickData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const zmagazynowaneHeaders = zmagazynowaneData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const wydanieHeaders = wydanieData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const przepakHeaders = przepakData[0]?.map((c) => (c && typeof c === 'object' ? c.value : c)) ?? [];
      const raportRows = raportData.slice(1);
      const psRows = psData.slice(1);
      const pickRows = pickData.slice(1);
      const zmagazynowaneRows = zmagazynowaneData.slice(1);
      const wydanieRows = wydanieData.slice(1);
      const przepakRows = przepakData.slice(1);

      const byDayRaport = aggregateRaportByDay(raportRows, raportHeaders);
      const byDayPs = aggregatePsByDay(psRows, psHeaders);
      const byDayPick = aggregatePickSheetByDay(pickRows, pickHeaders);
      const byDayZmagazynowane = aggregateZmagazynowaneByDay(zmagazynowaneRows, zmagazynowaneHeaders);
      const byDayWydanie = aggregateWydanieByDay(wydanieRows, wydanieHeaders);
      const byDayPrzepak = aggregatePrzepakByDay(przepakRows, przepakHeaders);
      dataRef.current = { byDayRaport, byDayPs, byDayPick, byDayZmagazynowane, byDayWydanie, byDayPrzepak, psRows, psHeaders };

      const raportDates = Object.keys(byDayRaport);
      const psDates = Object.keys(byDayPs);
      const pickDates = Object.keys(byDayPick);
      const zmagazynowaneDates = Object.keys(byDayZmagazynowane);
      const wydanieDates = Object.keys(byDayWydanie);
      const przepakDates = Object.keys(byDayPrzepak);
      const allDates = [...new Set([...raportDates, ...psDates, ...pickDates, ...zmagazynowaneDates, ...wydanieDates, ...przepakDates])];
      const monthSet = new Set();
      allDates.forEach((d) => {
        if (d && d.length >= 7) monthSet.add(d.slice(0, 7));
      });
      const months = Array.from(monthSet).sort().reverse();
      setAvailableMonths(months);

      const monthToUse = months[0] || '';
      setSelectedMonth(monthToUse);

      const agg = aggregateByMonth(byDayRaport, byDayPs, byDayPick, byDayZmagazynowane, byDayWydanie, byDayPrzepak, monthToUse);
      setMonthly(agg);
    } catch (err) {
      console.error('Błąd ładowania podsumowania:', err);
      setError('Nie udało się załadować danych raportów.');
      setMonthly({ bhp: null, pick: null, repPick: null, przepak: null, transport: null, inbound: null, psSum: null, wydanie: null });
      setAvailableMonths([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleMonthChange = useCallback((monthStr) => {
    setSelectedMonth(monthStr || '');
    if (!monthStr) {
      setMonthly({ bhp: null, pick: null, repPick: null, przepak: null, transport: null, inbound: null, psSum: null, wydanie: null });
      return;
    }
    const { byDayRaport, byDayPs, byDayPick, byDayZmagazynowane, byDayWydanie, byDayPrzepak } = dataRef.current;
    const agg = aggregateByMonth(byDayRaport, byDayPs, byDayPick, byDayZmagazynowane, byDayWydanie, byDayPrzepak, monthStr);
    setMonthly(agg);
  }, []);

  const openDrawer = useCallback((kpi) => {
    setDrawerKpi(kpi);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setDrawerKpi(null);
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;
    const onEsc = (e) => e.key === 'Escape' && closeDrawer();
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [drawerOpen, closeDrawer]);

  useEffect(() => {
    if (drawerOpen && chartPanelRef.current) {
      chartPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [drawerOpen, drawerKpi]);

  const chartData = useMemo(() => {
    if (!drawerKpi || !selectedMonth) return [];
    return getChartDataForKpi(drawerKpi, selectedMonth, dataRef);
  }, [drawerKpi, selectedMonth]);

  /** Wiersze PS dla wybranego miesiąca (tylko gdy drawerKpi === 'PS') */
  const psTableRows = useMemo(() => {
    if (drawerKpi !== 'PS' || !selectedMonth) return [];
    const { psRows, psHeaders } = dataRef.current;
    return filterPsRowsByMonth(psRows, psHeaders, selectedMonth);
  }, [drawerKpi, selectedMonth]);

  /** PS według kodowania – dane do wykresu (tylko gdy drawerKpi === 'PS') */
  const psOpisKodowaniaData = useMemo(() => {
    if (drawerKpi !== 'PS' || !psTableRows.length) return [];
    return buildOpisKodowaniaBarData(psTableRows, dataRef.current.psHeaders || []);
  }, [drawerKpi, psTableRows]);

  /** PS według PSA – dane do wykresu (tylko gdy drawerKpi === 'PS') */
  const psPsaData = useMemo(() => {
    if (drawerKpi !== 'PS' || !psTableRows.length) return [];
    return buildPsaBarData(psTableRows, dataRef.current.psHeaders || []);
  }, [drawerKpi, psTableRows]);

  /** ZMAGAZYNOWANE – dane do wykresu stacked (dzienne z podziałem na magazyny) */
  const zmagazynowaneChartData = useMemo(() => {
    if (drawerKpi !== 'ZMAGAZYNOWANE' || !selectedMonth) return { data: [], magazyny: [] };
    return getZmagazynowaneChartData(selectedMonth, dataRef);
  }, [drawerKpi, selectedMonth]);

  /** WYDANIE – dane do wykresu stacked (dzienne z podziałem na magazyny) */
  const wydanieChartData = useMemo(() => {
    if (drawerKpi !== 'WYDANIE' || !selectedMonth) return { data: [], magazyny: [] };
    return getWydanieChartData(selectedMonth, dataRef);
  }, [drawerKpi, selectedMonth]);

  /** PRZEPAK – dane do wykresu stacked (dzienne z podziałem na magazyny) */
  const przepakChartData = useMemo(() => {
    if (drawerKpi !== 'PRZEPAK' || !selectedMonth) return { data: [], magazyny: [] };
    return getPrzepakChartData(selectedMonth, dataRef);
  }, [drawerKpi, selectedMonth]);

  const transportChartData = useMemo(() => {
    if (drawerKpi !== 'TRANSPORT' || !selectedMonth) return { data: [] };
    return getTransportChartData(selectedMonth, dataRef);
  }, [drawerKpi, selectedMonth]);

  const ZMAGAZYNOWANE_MAGAZYN_COLORS = ['#9c27b0', '#26a69a', '#ff9800', '#42a5f5', '#5c6bc0', '#00bcd4', '#e65100', '#78909c'];
  const WYDANIE_MAGAZYN_COLORS = ['#ff9800', '#26a69a', '#42a5f5', '#9c27b0', '#5c6bc0', '#00bcd4', '#e65100', '#78909c'];
  const PRZEPAK_MAGAZYN_COLORS = ['#00bcd4', '#26a69a', '#ff9800', '#42a5f5', '#9c27b0', '#5c6bc0', '#e65100', '#78909c'];

  /** Zawsze zwraca string – zapobiega React error #130 (Objects are not valid as React child) */
  const safeRender = (v) => {
    if (v == null) return '';
    if (typeof v === 'object') return v instanceof Date ? v.toLocaleDateString('pl-PL') : String(v);
    return String(v);
  };
  const fmt = (v) => (v != null && typeof v !== 'object' ? String(v) : (v != null ? safeRender(v) : '—'));
  const fmtNum = (v) => (v != null && typeof v === 'number' ? String(Math.round(v)) : (v != null ? safeRender(v) : '—'));

  if (loading && !monthly.psSum && monthly.bhp == null && monthly.pick == null) {
    return (
      <div className="kpi-stat-page kpi-dashboard-tremor">
        <div className="kpi-stat-page-header">
          <Title className="kpi-stat-page-title">Podsumowanie miesiąca</Title>
          <Text className="kpi-stat-page-subtitle">Ładowanie…</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kpi-stat-page kpi-dashboard-tremor">
        <div className="kpi-stat-page-header">
          <Title className="kpi-stat-page-title">Podsumowanie miesiąca</Title>
          <Text className="kpi-stat-page-subtitle kpi-dashboard-error">{error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="kpi-stat-page kpi-dashboard-tremor kpi-dashboard-home">
      <header className="kpi-dashboard-header kpi-dashboard-home-header">
        <div className="kpi-dashboard-report-title-row kpi-dashboard-report-title-row-three">
          <div className="kpi-dashboard-report-title-left">
            <img src={logoMcg} alt="MCG" className="kpi-dashboard-header-logo" />
            <img src={logoMan} alt="MAN" className="kpi-dashboard-header-logo" />
          </div>
          <div className="kpi-dashboard-report-title-center">
            <Title className="kpi-stat-page-title">Podsumowanie miesiąca</Title>
            <span className="kpi-dashboard-report-date">
              {selectedMonth ? formatMonthLabel(selectedMonth) : 'Wybierz miesiąc'}
            </span>
          </div>
          <div className="kpi-dashboard-report-title-right">
            <MonthPicker
              value={selectedMonth}
              availableMonths={availableMonths}
              onChange={handleMonthChange}
            />
          </div>
        </div>
      </header>

      {availableMonths.length === 0 ? (
        <Card className="kpi-stat-card">
          <Text className="kpi-dashboard-chart-empty">
            Dodaj arkusz „Raport” lub „PS” w Uzupełnij raport i wgraj dane, aby zobaczyć Podsumowanie miesiąca.
          </Text>
        </Card>
      ) : (
        <Grid numItemsSm={1} numItemsMd={2} numItemsLg={8} className="kpi-home-cards kpi-podsumowanie-cards" style={{ marginTop: 24 }}>
          <Card
            className={`kpi-home-card kpi-home-card-bhp kpi-home-card-glass kpi-home-card-glass-green kpi-podsumowanie-card-clickable${drawerKpi === 'BHP' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('BHP')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('BHP')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">BHP</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.bhp)}</Metric>
          </Card>

          <Card
            className={`kpi-home-card kpi-home-card-ps kpi-home-card-glass kpi-home-card-glass-green kpi-podsumowanie-card-clickable${drawerKpi === 'PS' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('PS')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('PS')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">PS</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.psSum)}</Metric>
          </Card>

          <Card
            className={`kpi-home-card kpi-home-card-inbound kpi-home-card-glass kpi-home-card-glass-green kpi-podsumowanie-card-clickable${drawerKpi === 'ZMAGAZYNOWANE' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('ZMAGAZYNOWANE')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('ZMAGAZYNOWANE')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">ZMAGAZYNOWANE</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.inbound)}</Metric>
          </Card>

          <Card
            className={`kpi-home-card kpi-home-card-pick kpi-podsumowanie-card-clickable${drawerKpi === 'PICK' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('PICK')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('PICK')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">PICK</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.pick)}</Metric>
          </Card>

          <Card
            className={`kpi-home-card kpi-home-card-oww kpi-podsumowanie-card-clickable${drawerKpi === 'WYDANIE' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('WYDANIE')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('WYDANIE')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">WYDANIE</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.wydanie)}</Metric>
          </Card>

          <Card
            className={`kpi-home-card kpi-home-card-oww kpi-podsumowanie-card-clickable${drawerKpi === 'REPLENISHMENT' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('REPLENISHMENT')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('REPLENISHMENT')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">REPLENISHMENT</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.repPick)}</Metric>
          </Card>

          <Card
            className={`kpi-home-card kpi-home-card-pick kpi-podsumowanie-card-clickable${drawerKpi === 'PRZEPAK' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('PRZEPAK')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('PRZEPAK')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">PRZEPAK</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.przepak)}</Metric>
          </Card>

          <Card
            className={`kpi-home-card kpi-home-card-transport kpi-podsumowanie-card-clickable${drawerKpi === 'TRANSPORT' ? ' kpi-podsumowanie-card-active' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => openDrawer('TRANSPORT')}
            onKeyDown={(e) => e.key === 'Enter' && openDrawer('TRANSPORT')}
          >
            <span className="kpi-home-card-label-wrap">
              <Text className="kpi-home-card-label">TRANSPORT</Text>
            </span>
            <Metric className="kpi-home-metric">{fmtNum(monthly.transport)}</Metric>
          </Card>
        </Grid>
      )}

      {/* Moduł z wykresami – pod kafelkami KPI (pełna szerokość) */}
      {drawerOpen && (
        <div ref={chartPanelRef} className="kpi-podsumowanie-chart-panel">
          <div className="kpi-podsumowanie-chart-panel-header">
            <Title className="kpi-podsumowanie-chart-panel-title">
              {drawerKpi} – {selectedMonth ? formatMonthLabel(selectedMonth) : ''}
            </Title>
            <button
              type="button"
              className="kpi-podsumowanie-chart-panel-close"
              onClick={closeDrawer}
              aria-label="Zamknij"
            >
              ✕
            </button>
          </div>
          <div className="kpi-podsumowanie-chart-panel-body">
            {drawerKpi === 'ZMAGAZYNOWANE' && zmagazynowaneChartData.data.length > 0 ? (
              <Card className="kpi-podsumowanie-chart-panel-card">
                <div className="kpi-zmagazynowane-chart-header">
                  <Text className="kpi-podsumowanie-chart-panel-label">
                    {zmagazynowaneChartView === 'dwa' && 'Suma dzienna + podział na magazyny'}
                    {zmagazynowaneChartView === 'trendy' && 'Trendy ilości wg magazynów'}
                  </Text>
                  <div className="kpi-zmagazynowane-view-tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={zmagazynowaneChartView === 'dwa'}
                      className={`kpi-zmagazynowane-view-tab ${zmagazynowaneChartView === 'dwa' ? 'active' : ''}`}
                      onClick={() => setZmagazynowaneChartView('dwa')}
                    >
                      Dwa wykresy
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={zmagazynowaneChartView === 'trendy'}
                      className={`kpi-zmagazynowane-view-tab ${zmagazynowaneChartView === 'trendy' ? 'active' : ''}`}
                      onClick={() => setZmagazynowaneChartView('trendy')}
                    >
                      Trendy
                    </button>
                  </div>
                </div>
                {zmagazynowaneChartView === 'dwa' && (
                  <div className="kpi-zmagazynowane-dwa-charts">
                    <div className="kpi-zmagazynowane-dwa-chart">
                      <Text className="kpi-zmagazynowane-dwa-title">Suma dzienna</Text>
                      <div className="kpi-zmagazynowane-dwa-chart-plot">
                        <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                          <BarChart data={zmagazynowaneChartData.data} margin={{ top: 56, right: 16, left: 48, bottom: 48 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                            <XAxis {...KPI_DAILY_X_AXIS} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(156, 39, 176, 0.15)' }} />
                            <Bar {...KPI_BAR_LABELS_ALWAYS} dataKey="value" fill="#9c27b0" radius={[4, 4, 0, 0]}>
                              <LabelList dataKey="value" position="top" offset={8} content={(p) => (p.payload?.value != null ? <ChartValueLabel {...p} value={p.payload.value} dataKey="value" color="#9c27b0" /> : null)} />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="kpi-zmagazynowane-dwa-chart">
                      <Text className="kpi-zmagazynowane-dwa-title">Podział na magazyny</Text>
                      <div className="kpi-zmagazynowane-dwa-chart-plot">
                        <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                          <BarChart data={zmagazynowaneChartData.data} margin={{ top: 64, right: 16, left: 48, bottom: 48 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                            <XAxis {...KPI_DAILY_X_AXIS} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(156, 39, 176, 0.15)' }} />
                            <Legend wrapperStyle={{ paddingTop: 4 }} formatter={(v) => (v && !String(v).startsWith('_') ? <span style={{ color: '#e2e8f0', fontSize: 11 }}>{v}</span> : null)} />
                            {zmagazynowaneChartData.magazyny.map((mag, i) => {
                              const isLast = i === zmagazynowaneChartData.magazyny.length - 1;
                              return (
                                <Bar
                                  key={mag}
                                  {...KPI_BAR_LABELS_ALWAYS}
                                  dataKey={mag}
                                  name={mag}
                                  stackId="magazyn"
                                  fill={ZMAGAZYNOWANE_MAGAZYN_COLORS[i % ZMAGAZYNOWANE_MAGAZYN_COLORS.length]}
                                  radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                >
                                  <StackedBarSumLabelList stackKeysBottomToTop={zmagazynowaneChartData.magazyny} segmentKey={mag} sumColor="#9c27b0" />
                                </Bar>
                              );
                            })}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
                {zmagazynowaneChartView === 'trendy' && (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={zmagazynowaneChartData.data} margin={{ top: 16, right: 16, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                      <XAxis {...KPI_DAILY_X_AXIS} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<ZmagazynowaneTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: 8 }} formatter={(v) => (v && !String(v).startsWith('_') ? <span style={{ color: '#e2e8f0', fontSize: 12 }}>{v}</span> : null)} />
                      {zmagazynowaneChartData.magazyny.map((mag, i) => (
                        <Line
                          key={mag}
                          type="monotone"
                          dataKey={mag}
                          name={mag}
                          stroke={ZMAGAZYNOWANE_MAGAZYN_COLORS[i % ZMAGAZYNOWANE_MAGAZYN_COLORS.length]}
                          strokeWidth={2}
                          dot={{ fill: ZMAGAZYNOWANE_MAGAZYN_COLORS[i % ZMAGAZYNOWANE_MAGAZYN_COLORS.length], r: 3 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </Card>
            ) : drawerKpi === 'WYDANIE' && wydanieChartData.data.length > 0 ? (
              <Card className="kpi-podsumowanie-chart-panel-card">
                <div className="kpi-zmagazynowane-chart-header">
                  <Text className="kpi-podsumowanie-chart-panel-label">
                    {wydanieChartView === 'dwa' && 'Suma dzienna + podział na magazyny'}
                    {wydanieChartView === 'trendy' && 'Trendy ilości wg magazynów'}
                    {wydanieChartView === 'osobne' && 'Osobne wykresy wg magazynów'}
                  </Text>
                  <div className="kpi-zmagazynowane-view-tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={wydanieChartView === 'dwa'}
                      className={`kpi-zmagazynowane-view-tab ${wydanieChartView === 'dwa' ? 'active' : ''}`}
                      onClick={() => setWydanieChartView('dwa')}
                    >
                      Dwa wykresy
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={wydanieChartView === 'trendy'}
                      className={`kpi-zmagazynowane-view-tab ${wydanieChartView === 'trendy' ? 'active' : ''}`}
                      onClick={() => setWydanieChartView('trendy')}
                    >
                      Trendy
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={wydanieChartView === 'osobne'}
                      className={`kpi-zmagazynowane-view-tab ${wydanieChartView === 'osobne' ? 'active' : ''}`}
                      onClick={() => setWydanieChartView('osobne')}
                    >
                      Osobne magazyny
                    </button>
                  </div>
                </div>
                {wydanieChartView === 'dwa' && (
                  <div className="kpi-zmagazynowane-dwa-charts">
                    <div className="kpi-zmagazynowane-dwa-chart">
                      <Text className="kpi-zmagazynowane-dwa-title">Suma dzienna</Text>
                      <div className="kpi-zmagazynowane-dwa-chart-plot">
                        <KpiDailyTotalsHtmlRow data={wydanieChartData.data} field="value" color="#ff9800" />
                        <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                          <BarChart data={wydanieChartData.data} margin={KPI_DWA_MARGIN_DAILY}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                            <XAxis {...KPI_DAILY_X_AXIS} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(255, 152, 0, 0.15)' }} />
                            <Bar {...KPI_BAR_LABELS_ALWAYS} dataKey="value" fill="#ff9800" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="kpi-zmagazynowane-dwa-chart">
                      <Text className="kpi-zmagazynowane-dwa-title">Podział na magazyny</Text>
                      <div className="kpi-zmagazynowane-dwa-chart-plot">
                        <KpiDailyTotalsHtmlRow data={wydanieChartData.data} field="_sumLabel" color="#ff9800" />
                        <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                          <BarChart data={wydanieChartData.data} margin={KPI_DWA_MARGIN_STACK}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                            <XAxis {...KPI_DAILY_X_AXIS} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(255, 152, 0, 0.15)' }} />
                            <Legend wrapperStyle={{ paddingTop: 4 }} formatter={(v) => (v && !String(v).startsWith('_') ? <span style={{ color: '#e2e8f0', fontSize: 11 }}>{v}</span> : null)} />
                            {wydanieChartData.magazyny.map((mag, i) => {
                              const isLast = i === wydanieChartData.magazyny.length - 1;
                              return (
                                <Bar
                                  key={mag}
                                  {...KPI_BAR_LABELS_ALWAYS}
                                  dataKey={mag}
                                  name={mag}
                                  stackId="magazyn"
                                  fill={WYDANIE_MAGAZYN_COLORS[i % WYDANIE_MAGAZYN_COLORS.length]}
                                  radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                />
                              );
                            })}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
                {wydanieChartView === 'trendy' && (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={wydanieChartData.data} margin={{ top: 16, right: 16, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                      <XAxis {...KPI_DAILY_X_AXIS} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<ZmagazynowaneTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: 8 }} formatter={(v) => (v && !String(v).startsWith('_') ? <span style={{ color: '#e2e8f0', fontSize: 12 }}>{v}</span> : null)} />
                      {wydanieChartData.magazyny.map((mag, i) => (
                        <Line
                          key={mag}
                          type="monotone"
                          dataKey={mag}
                          name={mag}
                          stroke={WYDANIE_MAGAZYN_COLORS[i % WYDANIE_MAGAZYN_COLORS.length]}
                          strokeWidth={2}
                          dot={{ fill: WYDANIE_MAGAZYN_COLORS[i % WYDANIE_MAGAZYN_COLORS.length], r: 3 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
                {wydanieChartView === 'osobne' && (
                  <div className="kpi-zmagazynowane-dwa-charts kpi-osobne-magazyny-charts">
                    {wydanieChartData.magazyny.map((mag, i) => (
                      <div key={mag} className="kpi-zmagazynowane-dwa-chart">
                        <Text className="kpi-zmagazynowane-dwa-title">Magazyn {mag}</Text>
                        <div className="kpi-zmagazynowane-dwa-chart-plot">
                          <KpiDailyTotalsHtmlRow data={wydanieChartData.data} field={mag} color={WYDANIE_MAGAZYN_COLORS[i % WYDANIE_MAGAZYN_COLORS.length]} />
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={wydanieChartData.data} margin={KPI_OSOBNE_BAR_MARGIN}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                              <XAxis {...KPI_DAILY_X_AXIS} />
                              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
                              <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(255, 152, 0, 0.15)' }} />
                              <Bar
                                {...KPI_BAR_LABELS_ALWAYS}
                                dataKey={mag}
                                name={mag}
                                fill={WYDANIE_MAGAZYN_COLORS[i % WYDANIE_MAGAZYN_COLORS.length]}
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ) : drawerKpi === 'PRZEPAK' && przepakChartData.data.length > 0 ? (
              <Card className="kpi-podsumowanie-chart-panel-card">
                <div className="kpi-zmagazynowane-chart-header">
                  <Text className="kpi-podsumowanie-chart-panel-label">
                    {przepakChartView === 'dwa' && 'Suma dzienna + podział na magazyny'}
                    {przepakChartView === 'trendy' && 'Trendy ilości wg magazynów'}
                    {przepakChartView === 'osobne' && 'Osobne wykresy wg magazynów'}
                  </Text>
                  <div className="kpi-zmagazynowane-view-tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={przepakChartView === 'dwa'}
                      className={`kpi-zmagazynowane-view-tab ${przepakChartView === 'dwa' ? 'active' : ''}`}
                      onClick={() => setPrzepakChartView('dwa')}
                    >
                      Dwa wykresy
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={przepakChartView === 'trendy'}
                      className={`kpi-zmagazynowane-view-tab ${przepakChartView === 'trendy' ? 'active' : ''}`}
                      onClick={() => setPrzepakChartView('trendy')}
                    >
                      Trendy
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={przepakChartView === 'osobne'}
                      className={`kpi-zmagazynowane-view-tab ${przepakChartView === 'osobne' ? 'active' : ''}`}
                      onClick={() => setPrzepakChartView('osobne')}
                    >
                      Osobne magazyny
                    </button>
                  </div>
                </div>
                {przepakChartView === 'dwa' && (
                  <div className="kpi-zmagazynowane-dwa-charts">
                    <div className="kpi-zmagazynowane-dwa-chart">
                      <Text className="kpi-zmagazynowane-dwa-title">Suma dzienna</Text>
                      <div className="kpi-zmagazynowane-dwa-chart-plot">
                        <KpiDailyTotalsHtmlRow data={przepakChartData.data} field="value" color="#00bcd4" />
                        <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                          <BarChart data={przepakChartData.data} margin={KPI_DWA_MARGIN_DAILY}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                            <XAxis {...KPI_DAILY_X_AXIS} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(0, 188, 212, 0.15)' }} />
                            <Bar {...KPI_BAR_LABELS_ALWAYS} dataKey="value" fill="#00bcd4" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    <div className="kpi-zmagazynowane-dwa-chart">
                      <Text className="kpi-zmagazynowane-dwa-title">Podział na magazyny</Text>
                      <div className="kpi-zmagazynowane-dwa-chart-plot">
                        <KpiDailyTotalsHtmlRow data={przepakChartData.data} field="_sumLabel" color="#00bcd4" />
                        <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                          <BarChart data={przepakChartData.data} margin={KPI_DWA_MARGIN_STACK}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                            <XAxis {...KPI_DAILY_X_AXIS} />
                            <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                            <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(0, 188, 212, 0.15)' }} />
                            <Legend wrapperStyle={{ paddingTop: 4 }} formatter={(v) => (v && !String(v).startsWith('_') ? <span style={{ color: '#e2e8f0', fontSize: 11 }}>{v}</span> : null)} />
                            {przepakChartData.magazyny.map((mag, i) => {
                              const isLast = i === przepakChartData.magazyny.length - 1;
                              return (
                                <Bar
                                  key={mag}
                                  {...KPI_BAR_LABELS_ALWAYS}
                                  dataKey={mag}
                                  name={mag}
                                  stackId="magazyn"
                                  fill={PRZEPAK_MAGAZYN_COLORS[i % PRZEPAK_MAGAZYN_COLORS.length]}
                                  radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                                />
                              );
                            })}
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}
                {przepakChartView === 'trendy' && (
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={przepakChartData.data} margin={{ top: 16, right: 16, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                      <XAxis {...KPI_DAILY_X_AXIS} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                      <Tooltip content={<ZmagazynowaneTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: 8 }} formatter={(v) => (v && !String(v).startsWith('_') ? <span style={{ color: '#e2e8f0', fontSize: 12 }}>{v}</span> : null)} />
                      {przepakChartData.magazyny.map((mag, i) => (
                        <Line
                          key={mag}
                          type="monotone"
                          dataKey={mag}
                          name={mag}
                          stroke={PRZEPAK_MAGAZYN_COLORS[i % PRZEPAK_MAGAZYN_COLORS.length]}
                          strokeWidth={2}
                          dot={{ fill: PRZEPAK_MAGAZYN_COLORS[i % PRZEPAK_MAGAZYN_COLORS.length], r: 3 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
                {przepakChartView === 'osobne' && (
                  <div className="kpi-zmagazynowane-dwa-charts kpi-osobne-magazyny-charts">
                    {przepakChartData.magazyny.map((mag, i) => (
                      <div key={mag} className="kpi-zmagazynowane-dwa-chart">
                        <Text className="kpi-zmagazynowane-dwa-title">Magazyn {mag}</Text>
                        <div className="kpi-zmagazynowane-dwa-chart-plot">
                          <KpiDailyTotalsHtmlRow data={przepakChartData.data} field={mag} color={PRZEPAK_MAGAZYN_COLORS[i % PRZEPAK_MAGAZYN_COLORS.length]} />
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={przepakChartData.data} margin={KPI_OSOBNE_BAR_MARGIN}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                              <XAxis {...KPI_DAILY_X_AXIS} />
                              <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} allowDecimals={false} />
                              <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(0, 188, 212, 0.15)' }} />
                              <Bar
                                {...KPI_BAR_LABELS_ALWAYS}
                                dataKey={mag}
                                name={mag}
                                fill={PRZEPAK_MAGAZYN_COLORS[i % PRZEPAK_MAGAZYN_COLORS.length]}
                                radius={[4, 4, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            ) : drawerKpi === 'TRANSPORT' && selectedMonth && transportChartData.data.length > 0 ? (
              <Card className="kpi-podsumowanie-chart-panel-card">
                <Text className="kpi-podsumowanie-chart-panel-label">
                  Suma dzienna (SQ + REP + LERGUT) oraz udział każdej składowej w dniu
                </Text>
                <div className="kpi-zmagazynowane-dwa-charts">
                  <div className="kpi-zmagazynowane-dwa-chart">
                    <Text className="kpi-zmagazynowane-dwa-title">Suma dzienna</Text>
                    <div className="kpi-zmagazynowane-dwa-chart-plot">
                      <KpiDailyTotalsHtmlRow data={transportChartData.data} field="value" color={KPI_CHART_COLORS.TRANSPORT} />
                      <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                        <BarChart data={transportChartData.data} margin={KPI_DWA_MARGIN_DAILY}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                          <XAxis {...KPI_DAILY_X_AXIS} />
                          <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                          <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(38, 166, 154, 0.15)' }} />
                          <Bar {...KPI_BAR_LABELS_ALWAYS} dataKey="value" name="Suma" fill={KPI_CHART_COLORS.TRANSPORT} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="kpi-zmagazynowane-dwa-chart">
                    <Text className="kpi-zmagazynowane-dwa-title">SHIP SQ, SHIP REP, SHIP LERGUT (dzień)</Text>
                    <div className="kpi-zmagazynowane-dwa-chart-plot">
                      <KpiDailyTotalsHtmlRow data={transportChartData.data} field="_sumLabel" color={KPI_CHART_COLORS.TRANSPORT} />
                      <ResponsiveContainer width="100%" height={dwaChartPlotHeight}>
                        <BarChart data={transportChartData.data} margin={KPI_DWA_MARGIN_STACK}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                          <XAxis {...KPI_DAILY_X_AXIS} />
                          <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                          <Tooltip content={<ZmagazynowaneTooltip />} cursor={{ fill: 'rgba(38, 166, 154, 0.15)' }} />
                          <Legend wrapperStyle={{ paddingTop: 4 }} formatter={(v) => (v && !String(v).startsWith('_') ? <span style={{ color: '#e2e8f0', fontSize: 11 }}>{v}</span> : null)} />
                          {TRANSPORT_STACK_SERIES.map((s, i) => {
                            const isLast = i === TRANSPORT_STACK_SERIES.length - 1;
                            return (
                              <Bar
                                key={s.key}
                                {...KPI_BAR_LABELS_ALWAYS}
                                dataKey={s.key}
                                name={s.name}
                                stackId="transport"
                                fill={s.color}
                                radius={isLast ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                              />
                            );
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </Card>
            ) : chartData.length > 0 ? (
              <Card className="kpi-podsumowanie-chart-panel-card">
                <Text className="kpi-podsumowanie-chart-panel-label">
                  {drawerKpi === 'PS' ? 'Ilość wpisów wg czasu na dzień' : 'Wartości dzienne w miesiącu'}
                </Text>
                <ResponsiveContainer width="100%" height={drawerKpi === 'REPLENISHMENT' ? 300 : 320}>
                  <BarChart data={chartData} margin={{ top: 52, right: 16, left: 0, bottom: 48 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.6} />
                    <XAxis {...KPI_DAILY_X_AXIS} />
                    <YAxis domain={drawerKpi === 'REPLENISHMENT' ? [0, 300] : undefined} tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      content={<PsTooltipDaily />}
                      cursor={{ fill: 'rgba(0, 188, 212, 0.15)' }}
                    />
                    <Bar
                      {...KPI_BAR_LABELS_ALWAYS}
                      dataKey="value"
                      name={drawerKpi === 'PS' ? 'Ilość wpisów' : drawerKpi}
                      fill={KPI_CHART_COLORS[drawerKpi] || '#00bcd4'}
                      radius={[4, 4, 0, 0]}
                    >
                      <LabelList
                        dataKey="value"
                        position="top"
                        offset={8}
                        content={(p) => <ChartValueLabel {...p} dataKey="value" color={KPI_CHART_COLORS[drawerKpi] || '#00bcd4'} />}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            ) : drawerKpi === 'ZMAGAZYNOWANE' ? (
              <Text className="kpi-dashboard-chart-empty">
                Brak arkusza „Zmagazynowane” lub brak danych. Dodaj arkusz z kolumnami: CONF (data potwierdzenia), MAGAZYN, ILOŚĆ.
              </Text>
            ) : drawerKpi === 'WYDANIE' ? (
              <Text className="kpi-dashboard-chart-empty">
                Brak arkusza „Wydanie” lub brak danych. Dodaj arkusz w Uzupełnij raport z kolumnami: DATA, MAGAZYN, ILOŚĆ.
              </Text>
            ) : drawerKpi === 'PRZEPAK' ? (
              <Text className="kpi-dashboard-chart-empty">
                Brak arkusza „Przepak” lub brak danych. Dodaj arkusz w Uzupełnij raport z kolumnami: DATA, MAGAZYN, ILOŚĆ.
              </Text>
            ) : drawerKpi === 'TRANSPORT' ? (
              <Text className="kpi-dashboard-chart-empty">
                Wybierz miesiąc lub uzupełnij arkusz „Raport” z kolumnami: DATA, SHIP SQ, SHIP REP, SHIP LERGUT.
              </Text>
            ) : (
              <Text className="kpi-dashboard-chart-empty">Brak danych do wykresu dla wybranego miesiąca.</Text>
            )}

            {drawerKpi === 'PS' && (psOpisKodowaniaData.length > 0 || psPsaData.length > 0) && (
              <div className="kpi-podsumowanie-ps-charts-grid">
                <Card className="kpi-podsumowanie-ps-mini-chart-card">
                  <Text className="kpi-podsumowanie-ps-mini-chart-title">PS według kodowania</Text>
                  {psOpisKodowaniaData.length === 0 ? (
                    <Text className="kpi-dashboard-chart-empty">Brak danych</Text>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(200, Math.min(psOpisKodowaniaData.length * 36, 360))}>
                      <BarChart
                        layout="vertical"
                        data={psOpisKodowaniaData}
                        margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.5} />
                        <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={220}
                          tick={{ fill: '#e2e8f0', fontSize: 12, fontWeight: 500 }}
                          tickFormatter={(v) => (v && v.length > 28 ? `${v.slice(0, 25)}…` : v)}
                          interval={0}
                        />
                        <Tooltip content={<PsTooltipOpisKodowania />} cursor={{ fill: 'rgba(66, 165, 245, 0.15)' }} />
                        <Bar {...KPI_BAR_LABELS_ALWAYS} dataKey="value" name="Ilość wpisów" radius={[0, 4, 4, 0]} barSize={20} minPointSize={4}>
                          {psOpisKodowaniaData.map((_, i) => (
                            <Cell key={i} fill={['#42a5f5', '#5c6bc0', '#26a69a', '#ff9800', '#9c27b0', '#00bcd4', '#78909c', '#e65100'][i % 8]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
                <Card className="kpi-podsumowanie-ps-mini-chart-card">
                  <Text className="kpi-podsumowanie-ps-mini-chart-title">PS według PSA</Text>
                  {psPsaData.length === 0 ? (
                    <Text className="kpi-dashboard-chart-empty">Brak danych</Text>
                  ) : (
                    <ResponsiveContainer width="100%" height={Math.max(200, Math.min(psPsaData.length * 36, 360))}>
                      <BarChart
                        layout="vertical"
                        data={psPsaData}
                        margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" strokeOpacity={0.5} />
                        <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <YAxis
                          type="category"
                          dataKey="PSA"
                          width={220}
                          tick={{ fill: '#e2e8f0', fontSize: 12, fontWeight: 500 }}
                          tickFormatter={(v) => (v && v.length > 28 ? `${v.slice(0, 25)}…` : v)}
                          interval={0}
                        />
                        <Tooltip content={<PsTooltipPsa />} cursor={{ fill: 'rgba(66, 165, 245, 0.15)' }} />
                        <Bar {...KPI_BAR_LABELS_ALWAYS} dataKey="count" name="Ilość wpisów" radius={[0, 4, 4, 0]} barSize={20} minPointSize={4}>
                          {psPsaData.map((_, i) => (
                            <Cell key={i} fill={['#26a69a', '#42a5f5', '#ff9800', '#9c27b0', '#5c6bc0', '#00bcd4', '#e65100', '#78909c'][i % 8]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>
              </div>
            )}

            {drawerKpi === 'PS' && (
              <Card className="kpi-podsumowanie-ps-errors-card">
                <Title className="kpi-podsumowanie-ps-errors-title">Błędy Lista</Title>
                {!dataRef.current.psHeaders?.length ? (
                  <Text className="kpi-dashboard-chart-empty">Brak arkusza PS lub brak danych.</Text>
                ) : psTableRows.length === 0 ? (
                  <Text className="kpi-dashboard-chart-empty">Brak wpisów za {selectedMonth ? formatMonthLabel(selectedMonth) : 'wybrany miesiąc'}.</Text>
                ) : (
                  <div className="kpi-podsumowanie-ps-table-wrap">
                    <Table className="kpi-podsumowanie-ps-table">
                      <TableHead>
                        <TableRow>
                          {dataRef.current.psHeaders.map((h, i) => (
                            <TableHeaderCell key={i}>{safeRender(typeof h === 'object' ? h?.value : h) || `Kolumna ${i + 1}`}</TableHeaderCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {psTableRows.map((row, idx) => (
                          <TableRow key={idx}>
                            {row.map((cell, c) => (
                              <TableCell key={c}>{safeRender(cell && typeof cell === 'object' ? cell.value : cell)}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default KPIPodsumowanie;
